import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import { BaseModal } from 'components/UI';
import Typography from '@material-ui/core/Typography';
import { vehicleActions } from 'state/modules/vehicles';
import { useStyles } from './ChangeVehicleStatusModal.styled';
import { VehicleStatusSelect, VehicleReasonsSelect } from 'components/DataFields';
import { CheckboxField, DateTimePickerField, TextareaField } from 'components/FormFields';
import { getDateTimezoneFormat } from 'utils/utils';

const statuses = ['INSHOP', 'RETIRED'];

const ChangeVehicleStatusModal = ({
  title,
  vehicle,
  vehicleId,
  vehicleIds,
  vehicleGroupId,
  timezone,
  open,
  handleClick,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const {
    onboarding: { errors },
    fetchingChangeStatus,
    fetchedChangeStatusSuccess,
  } = useSelector((state) => state.vehicles);
  const { localeFormatLower } = useSelector((state) => state.organizations);

  const ChangeVehicleStatusSchema = Yup.object().shape({
    status: Yup.string().required(i18n.t('common.required_field')),
    start_now: Yup.boolean(),
    forever: Yup.boolean(),
    start_time: Yup.string().when('start_now', {
      is: false,
      then: Yup.string().required(i18n.t('common.required_field')),
    }),
    end_time: Yup.string().when('forever', {
      is: false,
      then: Yup.string().required(i18n.t('common.required_field')),
    }),
    offline_reason: Yup.string().when('status', {
      is: (val) => statuses.indexOf(val) !== -1,
      then: Yup.string().required(i18n.t('common.required_field')),
    }),
    description: Yup.string().when('status', {
      is: (val) => statuses.indexOf(val) !== -1,
      then: Yup.string()
        .required(i18n.t('common.required_field'))
        .max(140, i18n.t('common.max_character', { max: 140 })),
    }),
  });

  useEffect(() => {
    if (fetchedChangeStatusSuccess) {
      handleClick();
    }
  }, [fetchedChangeStatusSuccess, handleClick, dispatch]);

  const submit = (values) => {
    const { start_now, forever, ...data } = values;
    // delete date fields if checks are selected
    if (start_now) delete data.start_time;
    if (forever) delete data.end_time;

    // remove empty fields
    Object.keys(data).forEach((key) => !data[key] && data[key] !== false && delete data[key]);

    if (vehicleGroupId) {
      data.vehicle_group_id = vehicleGroupId;
      dispatch(vehicleActions.changeMultipleVehicleStatus(data));
    } else if (vehicleIds && vehicleIds.length) {
      if (vehicleIds.length > 1) {
        data.vehicle_ids = vehicleIds;
        dispatch(vehicleActions.changeMultipleVehicleStatus(data));
      } else {
        dispatch(vehicleActions.changeVehicleStatus(data, vehicleIds[0]));
      }
    } else if (vehicleId) {
      dispatch(vehicleActions.changeVehicleStatus(data, vehicleId));
    }
  };
  const onSubmit = () => {
    formRef.current.submitForm();
  };

  const getDateFormat = () => getDateTimezoneFormat(`${localeFormatLower} h:mm a`, timezone);
  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      loading={fetchingChangeStatus}
      enterButtonLabel={i18n.t('vehicle.change_status')}
      title={title || i18n.t('vehicle.change_vehicle_status')}
      maxWidth={700}
    >
      <Formik
        initialValues={{
          status: vehicle?.status || '',
          start_now: true,
          forever: true,
          start_time: moment().format(),
          end_time: moment().add(1, 'day').format(),
          offline_reason: vehicle?.offline_reason || '',
          description: '',
        }}
        innerRef={formRef}
        validationSchema={ChangeVehicleStatusSchema}
        onSubmit={submit}
      >
        {({ handleSubmit, values }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Box mt={1}>
              <Field
                name="status"
                margin="none"
                label="Status"
                omitFields={['INACTIVE']}
                component={VehicleStatusSelect}
              />
            </Box>
            <Typography variant="h5" className={classes.subtitle}>
              {i18n.t('vehicle.date_range')}
            </Typography>
            <Box
              mt={1}
              display="flex"
              flexWrap="wrap"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <Field
                name="start_now"
                labelPlacement="end"
                component={CheckboxField}
                className={classes.checkField}
                label={i18n.t('vehicle.start_now')}
              />
              {!values.start_now && (
                <Field
                  editable={true}
                  name="start_time"
                  minDate={new Date()}
                  className={classes.dateField}
                  component={DateTimePickerField}
                  dateFormat={getDateFormat()}
                  asyncErrors={errors?.start_time}
                  label={i18n.t('vehicle.start_date')}
                />
              )}
              <Field
                name="forever"
                labelPlacement="end"
                component={CheckboxField}
                className={classes.checkField}
                label={i18n.t('vehicle.forever')}
              />
              {!values.forever && (
                <Field
                  name="end_time"
                  editable={true}
                  component={DateTimePickerField}
                  dateFormat={getDateFormat()}
                  asyncErrors={errors?.end_time}
                  className={classes.dateField}
                  minDate={moment(values.start_time).toDate() || new Date()}
                  label={i18n.t('vehicle.end_date')}
                />
              )}
            </Box>
            {statuses.indexOf(values.status) !== -1 && (
              <>
                <Box mt={1}>
                  <Field
                    margin="none"
                    name="offline_reason"
                    component={VehicleReasonsSelect}
                    className={classes.reasonSelect}
                    asyncErrors={errors?.offline_reason}
                    label={i18n.t('vehicle.offline_reason')}
                  />
                </Box>
                <Box mt={2}>
                  <Field
                    name="description"
                    component={TextareaField}
                    className={classes.reasonText}
                    asyncErrors={errors?.description}
                    label={i18n.t('vehicle.reason_description')}
                  />
                </Box>
              </>
            )}
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

ChangeVehicleStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  vehicleId: PropTypes.number,
  vehicleIds: PropTypes.array,
  vehicleGroupId: PropTypes.number,
};

ChangeVehicleStatusModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default ChangeVehicleStatusModal;
